import { FilterExpression, Pageable } from '@infuse/api/common';
import { AccessRequest, AccessRequestStatus, searchAccessRequests } from '@infuse/api/iam/access-request';
import { useInfiniteQuery } from '@tanstack/vue-query';
import { computed, ref, Ref } from 'vue';

const createSearchFilter = (searchQuery: string): FilterExpression | undefined => {
  const parts = searchQuery
    .toLowerCase()
    .split(' ')
    .map((part) => part.trim())
    .filter((part) => part !== '')
    .map((part) => `%${part}%`);
  if (parts.length === 0) {
    return undefined;
  }
  const expressions = parts.map((part) =>
    new FilterExpression().like('lower(requester.email)', part).or(new FilterExpression().like('lower(requester.name)', part))
  );
  return expressions.reduce((acc, cv) => acc.and(cv));
};

export interface UseAccessRequestsOptions {
  pageSize?: number;
  queryKey?: ReadonlyArray<unknown>;
  status?: Ref<AccessRequestStatus | undefined>;
  targetOrganisationId?: Ref<string | undefined>;
  targetTenantId?: Ref<string | undefined>;
  requesterUserId?: Ref<string | undefined>;
  enabled?: () => boolean;
}

export function useAccessRequests({
  pageSize = 20,
  queryKey = [],
  status,
  targetOrganisationId,
  targetTenantId,
  requesterUserId,
  enabled
}: UseAccessRequestsOptions = {}) {
  const searchQuery = ref<string | undefined>();

  const query = useInfiniteQuery<Pageable<AccessRequest>>({
    initialPageParam: 0,
    queryKey: [
      'access-requests',
      ...queryKey,
      status,
      targetOrganisationId,
      targetTenantId,
      requesterUserId,
      searchQuery
    ],
    enabled: () => (enabled ? enabled() : true),
    queryFn: async ({ pageParam }) => {
      const filterParts: FilterExpression[] = [];
      if (status?.value) {
        filterParts.push(new FilterExpression().equals('status', status.value, true)); // isEnum
      }
      if (targetOrganisationId?.value) {
        filterParts.push(new FilterExpression().equals('targetOrganisationId', targetOrganisationId.value));
      }
      if (targetTenantId?.value) {
        filterParts.push(new FilterExpression().equals('targetTenantId', targetTenantId.value));
      }
      if (requesterUserId?.value) {
        filterParts.push(new FilterExpression().equals('requesterUserId', requesterUserId.value));
      }
      if (searchQuery.value) {
        const search = createSearchFilter(searchQuery.value);
        if (search) {
          filterParts.push(search);
        }
      }
      const filter = filterParts.length === 0 ? undefined : filterParts.reduce((acc, cv) => acc.and(cv));

      return searchAccessRequests({
        size: pageSize,
        page: Number(pageParam),
        filter,
        sort: [{ key: 'requestedAt', descending: true }]
      });
    },
    getNextPageParam: (lastPage) => (lastPage.last ? undefined : lastPage.pageable.pageNumber + 1)
  });

  const accessRequests = computed(() => query.data.value?.pages.flatMap((page) => page.content) ?? []);

  return {
    ...query,
    searchQuery,
    accessRequests
  };
}
